import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Grid } from "@mui/material";


export const TableB = ({ leq1, leq5, leq12, doseAndMaxList, date, isSundayOrPH }) => {

  const generateTime = (index) => {
    if (index >= 0 && index <= 16) {
      return { time: `${index + 7}`, rowSpan: 17, date: '-' };
    } else {
      const hour = index - 17;
      return { time: `${hour}`, rowSpan: 7, date: '-' };
    }
  };

  // Manual parse to ensure NO timezone shifts from the browser
  const dateParts = date.split('-');
  const formattedToday = `${dateParts[2]}/${dateParts[1]}/${dateParts[0]}`;

  const currentDateObj = new Date(date + 'T00:00:00+08:00');
  const tomorrowObj = new Date(currentDateObj);
  tomorrowObj.setDate(currentDateObj.getDate() + 1);
  const formattedTomorrow = tomorrowObj.toLocaleDateString("en-GB");

  const baseFontSize = 13; // Define your base font size (in pixels)
  const minHeight = 650;   // The minimum window height to base the calculation on

  // Get the current window height
  const windowHeight = window.innerHeight;

  // Calculate the adjusted font size based on the window height
  const adjustedFontSize = baseFontSize * (windowHeight / minHeight);

  // Check if an hour index falls in the 7pm-7am period (evening/night)
  // index 0-11 = 7am-6pm (daytime, first 12h period)
  // index 12-23 = 7pm-6am (nighttime, second 12h period)
  const isNighttimePeriod = (index) => index >= 12;

  //Generate output for the noise dosage column
  const getDoseAndMax5ContentForTableB = (index, doseAndMaxList, leq1, isNoiseDose) => {
    // First data and non "B - Residential" noise dose and LEQ 5 max is always 12h
    if (index >= 1 && index <= 11) {
      return ""
    }

    // NEA standard: Sunday/PH 7pm-7am period -> show N.A.
    if (isSundayOrPH && isNighttimePeriod(index)) {
      if (index === 12) {
        return <td rowSpan={12}><br /><br />N.A.</td>;
      }
      return "";
    }

    let displayValue = "N.A.";
    if (doseAndMaxList[index]) {
      const data = doseAndMaxList[index];
      const value = isNoiseDose ? data.noise_dose_percentage : data.leq5_max;
      const roundingDP = isNoiseDose ? 2 : 1;
      const recordDate = new Date(date + 'T00:00:00+08:00');
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const isPastDate = recordDate.getTime() < today.getTime();

      //Index 0 denote a 12hr value.
      if (isPastDate && !isNoiseDose) {
        displayValue = "EOD";
      } else if (isEOD(new Date(date), data.timestamp)) {
        displayValue = "-";
      } else {
        displayValue = value === null
          ? "N.A."
          : (value === "-1.00" && !isNoiseDose) || (isNoiseDose && value === "101.00")
            ? "OL"
            : `${parseFloat(value).toFixed(roundingDP)}${isNoiseDose ? "%" : " dBA"}`;
      }

      //Handle cases where there is data mismatch
      if (index >= 12 && data.type_of_leq !== "1h" || index === 0 && data.type_of_leq !== "12h") {
        displayValue = "N.A.";
      }
    }

    if (index === 0 || doseAndMaxList.length === 2) {
      return <td rowSpan={12}><br /><br />{displayValue}</td>;
    } else {
      return <td>{displayValue}</td>;
    }
  };

  const buildMaps = (leq5Array, leq1Array) => {
    const leq5Map = {};
    const leq1Map = {};
    leq5Array.forEach(it => { leq5Map[new Date(it.timestamp).toISOString()] = it; });
    leq1Array.forEach(it => { leq1Map[new Date(it.timestamp).toISOString()] = it; });
    return { leq5Map, leq1Map };
  }

  const sgtToUtcIso = (dateKey, hour, minute) => {
    const parts = dateKey.split('-').map(x => parseInt(x, 10));
    let y = parts[0], m = parts[1] - 1, d = parts[2];

    // The 0:00 to 6:59 rows represent the NEXT calendar day in this noise day's table
    if (hour < 7) {
      d += 1;
    }

    const sgtMs = Date.UTC(y, m, d, hour, minute, 0);
    const utcMs = sgtMs - (8 * 3600 * 1000);
    return new Date(utcMs).toISOString();
  }

  const { leq5Map, leq1Map } = buildMaps(leq5, leq1);


  const adjustTime = (datetime) => {
    const currentDateTime = datetime;

    const hours = currentDateTime.getHours();

    // Handle time rounding and setting to 7AM
    if (hours >= 7 && hours < 19) {
      const startOf7AM = new Date(currentDateTime);
      startOf7AM.setHours(7, 0, 0, 0);
      return startOf7AM;
    } else {
      //Handle time rounding between 12am-7am and 7pm-12am
      const currentDateTimeCopy = new Date(currentDateTime);
      return new Date(currentDateTimeCopy.setHours(currentDateTimeCopy.getHours(), 0, 0, 0));
    }
  };

  const isEOD = (referenceTime, dataTime) => {
    const timeToNearestPeriod = adjustTime(referenceTime);
    return timeToNearestPeriod > new Date(dataTime);
  };

  return (
    <div style={{ marginBottom: "10px", minWidth: "100%", maxWidth: "100%" }}>

      <Grid container spacing={3} style={{ minWidth: "100%", maxWidth: "100%" }}>
        <Grid item xs={12} style={{ minWidth: "100%", maxWidth: "100%" }}>
          <Accordion defaultExpanded={true} style={{ backgroundColor: "#64748B", minWidth: "100%", maxWidth: "100%" }}>
            <AccordionSummary style={{ minWidth: "100%", maxWidth: "100%" }}
              expandIcon={< ExpandMoreIcon />}
              aria-controls="panel1a-content"
              id="panel1a-header"
            >
              <Typography color="white">{formattedToday}{isSundayOrPH ? " (Sunday / PH)" : ""}</Typography>
            </AccordionSummary>
            <AccordionDetails style={{ minWidth: "100%", maxWidth: "100%" }}>
              <Typography style={{ minWidth: "100%", maxWidth: "100%" }}>
                <table className="excel-table" style={{ backgroundColor: "white", fontSize: `${adjustedFontSize}px`, minWidth: "100%", maxWidth: "100%" }}>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Time (hr/5min)</th>
                      <th>00</th>
                      <th>05</th>
                      <th>10</th>
                      <th>15</th>
                      <th>20</th>
                      <th>25</th>
                      <th>30</th>
                      <th>35</th>
                      <th>40</th>
                      <th>45</th>
                      <th>50</th>
                      <th>55</th>
                      <th>Leq1hr/Accum</th>
                      <th>Leq12hr/Accum</th>
                      <th>Current Dose %</th>
                      <th>Max. Allow / Leq5min</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...Array(24)].map((_, index) => {
                      const { time, rowSpan } = generateTime(index);
                      return (
                        <tr>
                          {index === 0 ? <td rowSpan={rowSpan}>{formattedToday}</td> : index === 17 ? <td rowSpan={rowSpan}>{formattedTomorrow}</td> : ""}
                          <td>{time}:00</td>
                          {(() => {
                            const mins = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];
                            return mins.map(m => {
                              const utcIso = sgtToUtcIso(date, parseInt(time, 10), m);
                              const item = leq5Map[utcIso];
                              return item ? <td style={{ backgroundColor: item.has_exceeded_threshold ? 'red' : '' }} key={m}>{item.leq_value}</td> : <td key={m}>-</td>;
                            })
                          })()}
                          {/* Leq1hr/Accum column - NEA Category B:
                            Mon-Sat: 7am-6:59pm (index 0-11) = N.A., 7pm-6:59am (index 12-23) = show data
                            Sun/PH:  entire day = N.A. */}
                          {(() => {
                            // Sun/PH: Leq1hr is N.A. for entire day (7am to next 7am)
                            if (isSundayOrPH) {
                              return <td>N.A.</td>;
                            }
                            // Mon-Sat: Leq1hr is N.A. during daytime 7am-6:59pm
                            if (!isNighttimePeriod(index)) {
                              return <td>N.A.</td>;
                            }
                            // Mon-Sat 7pm-6:59am: show actual Leq1hr data
                            const utcIso = sgtToUtcIso(date, parseInt(time, 10), 0);
                            const item = leq1Map[utcIso];
                            return item ? <td>{item.leq_value}</td> : <td>-</td>;
                          })()}
                          {/* Leq12hr/Accum column - NEA Category B:
                            All days: 7am-6:59pm (index 0-11) = show data, 7pm-6:59am (index 12-23) = N.A. */}
                          {(() => {
                            // 7pm-6:59am (nighttime): Leq12hr is N.A. for ALL days
                            if (isNighttimePeriod(index)) {
                              return index === 12 ? <td rowSpan={12}><br /><br />N.A.</td> : "";
                            }
                            // 7am-6:59pm (daytime): show Leq12hr data
                            if (index === 0) {
                              return <td style={{ backgroundColor: leq12[0] && leq12[0].has_exceeded_threshold ? 'red' : '' }} rowSpan={12}><br /><br />{leq12[0] ? leq12[0].leq_value : "-"}</td>;
                            }
                            return "";
                          })()}
                          {getDoseAndMax5ContentForTableB(index, doseAndMaxList, leq1, true)}
                          {getDoseAndMax5ContentForTableB(index, doseAndMaxList, leq1, false)}
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>
    </div>
  )
}